import { useCallback, useEffect, useRef, useState } from "react";
import { sendMessage } from "../services/api";

export type StreamEvent =
  | { type: "start"; message?: string }
  | { type: "chunk"; content: string }
  | { type: "complete"; message?: string }
  | { type: "error"; message?: string; details?: string; retryable?: boolean }
  | { type: "retry"; attempt: number; maxRetries: number }
  | { type: "title_update"; threadId: string; title: string }
  | {
      type: "thread_update";
      threadId: string;
      messageCount?: number | null;
      lastMessageAt?: string | number | null;
    }
  | { type: "progress"; message: string }
  | {
      type: "outreach_draft";
      outreach: {
        outreachId: string;
        outreachUrl: string;
        recipient: { coachName: string; coachEmail: string; schoolName: string };
        subject: string;
      };
    }
  | { type: "profile_updated"; fields: Record<string, unknown> };

interface UseChatStreamOptions {
  onEvent?: (evt: StreamEvent) => void;
}

export function useChatStream({ onEvent }: UseChatStreamOptions) {
  const controllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const cancelledRef = useRef(false);
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const start = useCallback(
    async (args: { threadId: string; prompt: string }) => {
      if (isStreaming || isCancelling) {
        console.log("⚠️ Cannot start - already streaming or cancelling");
        return;
      }
      setIsStreaming(true);
      cancelledRef.current = false;

      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const resp = await sendMessage({
          threadId: args.threadId,
          prompt: args.prompt,
          stream: true,
          signal: controller.signal,
        });
        const reader = resp.body?.getReader();
        if (!reader) throw new Error("Streaming not supported");

        const decoder = new TextDecoder("utf-8");
        onEventRef.current?.({ type: "start", message: "Starting response..." });

        let buffer = "";
        let terminalEventReceived = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          if (cancelledRef.current) {
            console.log("🛑 Stream cancelled, stopping read loop");
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by "\n\n" and prefixed with "data: "
          const frames = buffer.split("\n\n");
          // Keep last partial frame in buffer
          buffer = frames.pop() || "";

          for (const frame of frames) {
            const line = frame.trim();
            if (!line.startsWith("data:")) continue;
            const json = line.replace(/^data:\s*/, "");
            try {
              const evt = JSON.parse(json) as StreamEvent;

              if (cancelledRef.current) {
                console.log("🛑 Ignoring event after cancellation:", evt.type);
                continue;
              }

              if (evt.type === "outreach_draft") {
                console.log("📧 [useChatStream] outreach_draft event parsed:", evt);
              }

              if (evt.type === "complete" || evt.type === "error") {
                terminalEventReceived = true;
              }

              onEventRef.current?.(evt);
            } catch (_) {
              // Ignore JSON parse errors for keepalive lines
            }
          }
        }

        if (!cancelledRef.current && !terminalEventReceived) {
          onEventRef.current?.({
            type: "error",
            message: "The response ended unexpectedly. Please try again.",
          });
        }
      } catch (error: any) {
        if (error?.name === "AbortError" || cancelledRef.current) {
          console.log("🛑 Stream aborted");
          // Don't send error event for intentional cancellations
        } else {
          onEventRef.current?.({ type: "error", message: error?.message || "Stream error" });
        }
      } finally {
        setIsStreaming(false);
        controllerRef.current = null;
        cancelledRef.current = false;
      }
    },
    [isStreaming, isCancelling]
  );

  const cancel = useCallback(async () => {
    console.log("🛑 Cancel called");
    cancelledRef.current = true;
    setIsCancelling(true);

    try {
      // Simply abort the fetch — server detects disconnect and aborts OpenAI request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    } catch (err) {
      console.error("🛑 Error during cancel:", err);
    } finally {
      setIsStreaming(false);
      setIsCancelling(false);
      console.log("🛑 Cancellation complete");
    }
  }, []);

  return { isStreaming, isCancelling, start, cancel };
}
